import {
    collection,
    type DocumentReference,
    doc,
    getDoc,
    limit,
    orderBy,
    query,
    where,
} from "firebase/firestore";
import { ref } from "firebase/storage";
import { supportedImageTypes } from "./common/utils/image.util";
import { db, storage } from "./Firebase";

export const conversationsCollection = "conversations";
export const messagesSubCollection = "messages";
export const internalNotesSubCollection = "notes";
export const aiSummariesSubCollection = "ai-summaries";
export const supportedAudioTypes = ["audio/m4a"];
export const supportedDocumentTypes = ["application/pdf"];
export const supportedAttachmentFileTypes = [
    ...supportedDocumentTypes,
    ...supportedImageTypes,
];
export const supportedFileTypes = [
    ...supportedDocumentTypes,
    ...supportedImageTypes,
    ...supportedAudioTypes,
];

export const conversationMessagesCollection = (conversationId: string) =>
    `${conversationsCollection}/${conversationId}/${messagesSubCollection}`;

export const conversationInternalNotesCollection = (conversationId: string) =>
    `${conversationsCollection}/${conversationId}/${internalNotesSubCollection}`;

export const conversationAiSummariesCollection = (conversationId: string) =>
    `${conversationsCollection}/${conversationId}/${aiSummariesSubCollection}`;

export const conversationFileRef = (
    filename: string,
    memberId: string,
    conversationId: string,
) => ref(storage, `conversation/${memberId}/${conversationId}/${filename}`);

export const conversationDocument = (conversationId: string) =>
    doc(db, conversationsCollection, conversationId);

export const agentAvatarFullRef = (agentId: string) =>
    ref(storage, `agent/avatar/full/${agentId}`);

export const agentAvatarThumbnailRef = (agentId: string) =>
    ref(storage, `agent/avatar/thumbnail/${agentId}`);

export const memberAvatarThumbnailRef = (memberId: string) =>
    ref(storage, `member/avatar/thumbnail/${memberId}`);

export const bookingImagesRef = (bookingId: string, uniqueId: string) =>
    ref(storage, `booking/${bookingId}/header/${uniqueId}`);

const catalogImageStorageBucket = import.meta.env.REACT_APP_CATALOG_IMAGE_BUCKET;

export const catalogImageRef = (uniqueId: string) =>
    ref(
        storage,
        `${catalogImageStorageBucket ? `${catalogImageStorageBucket}/` : ""}${uniqueId}`,
    );

export const bookingAttachmentRef = (
    fileId: string,
    bookingId: string,
    memberId: string,
) => ref(storage, `attachments/${memberId}/booking/${bookingId}/${fileId}`);

export const queryInternalNotesByRequest = (
    conversationId: string,
    requestId: string,
) =>
    query(
        collection(db, conversationInternalNotesCollection(conversationId)),
        orderBy("sentAt", "desc"),
        where("request", "==", requestId),
    );

export const queryOpenTasks = (conversationId: string) =>
    query(
        collection(db, conversationInternalNotesCollection(conversationId)),
        orderBy("sentAt", "desc"),
        where("mentionStatus", "==", "open"),
    );

export const queryMessageWithRequest = (
    conversationId: string,
    requestId: string,
    take: "first" | "last",
) =>
    query(
        collection(db, conversationMessagesCollection(conversationId)),
        orderBy("sentAt", take === "first" ? "asc" : "desc"),
        where("request", "==", requestId),
        limit(1),
    );

export const queryInternalNoteWithRequest = (
    conversationId: string,
    requestId: string,
    take: "first" | "last",
) =>
    query(
        collection(db, conversationInternalNotesCollection(conversationId)),
        orderBy("sentAt", take === "first" ? "asc" : "desc"),
        where("request", "==", requestId),
        limit(1),
    );

export const queryLatestMessage = (conversation: string, request: string | undefined) =>
    query(
        collection(db, conversationMessagesCollection(conversation)),
        orderBy("sentAt", "desc"),
        ...(request ? [where("request", "==", request)] : []),
        limit(1),
    );

export async function getDocumentOnce<T>(
    document: DocumentReference,
): Promise<T | undefined> {
    const snapshot = await getDoc(document);
    if (!snapshot.exists()) return undefined;
    return { ...snapshot.data(), id: snapshot.id } as unknown as T;
}
